"use client";

import { createContext, useContext, useMemo } from "react";
import { getDictionaryFor, type Dictionary } from "./index";
import { DEFAULT_LOCALE, INTL_LOCALE, type Locale } from "./types";

interface I18nValue {
    locale: Locale;
    dict: Dictionary;
    /** Locale do `Intl` — número, moeda e data seguem o idioma da interface. */
    intlLocale: string;
}

const I18nContext = createContext<I18nValue | null>(null);

/**
 * O locale vem do servidor (cookie) e desce por prop: sem flash de idioma errado na
 * hidratação e sem `useEffect` buscando preferência depois da tela já pintada.
 */
export function I18nProvider({ locale, children }: { locale: Locale; children: React.ReactNode }) {
    const value = useMemo<I18nValue>(
        () => ({ locale, dict: getDictionaryFor(locale), intlLocale: INTL_LOCALE[locale] }),
        [locale]
    );
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Fora do provider (componente montado isolado, teste, storybook) devolve pt-BR em vez
 * de lançar — string faltando nunca deve derrubar uma tela.
 */
export function useI18n(): I18nValue {
    return (
        useContext(I18nContext) ?? {
            locale: DEFAULT_LOCALE,
            dict: getDictionaryFor(DEFAULT_LOCALE),
            intlLocale: INTL_LOCALE[DEFAULT_LOCALE],
        }
    );
}

/** Açúcar para o caso comum: `const t = useT(); t.metaAds.kpiSpend`. */
export function useT(): Dictionary {
    return useI18n().dict;
}

/**
 * Rótulo de menu: a chave é o título em PT do item (fonte de verdade continua no
 * `sidebar.tsx`); sem tradução cadastrada, cai no original em vez de sumir.
 */
export function useNavLabel(): (title: string) => string {
    const { dict } = useI18n();
    return (title: string) => dict.nav[title] ?? title;
}

interface ColumnLike {
    id: string;
    label: string;
    tooltip?: string;
}

/**
 * Coluna do dashboard Meta Ads: a chave é o `id` da coluna (estável), nunca o texto.
 * `COLUMN_DEFS` permanece intocado — o idioma entra só onde o cabeçalho é pintado.
 */
export function useColumnText(): (col: ColumnLike) => { label: string; tooltip?: string } {
    const { dict } = useI18n();
    return (col) => {
        const override = dict.metaAdsColumns[col.id];
        return { label: override?.label ?? col.label, tooltip: override?.tooltip ?? col.tooltip };
    };
}
